import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { LoadingScreen } from '@/components/LoadingScreen'
import { useAuth } from '@/hooks/useAuth'
import { takeIntendedRoute } from '@/lib/intended-route'
import { EmailStep, ForgotConfirmStep, ForgotStep, NewPasswordStep, OptionsStep } from './LoginSteps'
import { useLoginFlow } from './useLoginFlow'
import './Login.css'

/**
 * Login page (ADR-0011 amendment). Collects credentials on this page and
 * authenticates directly against Cognito via SRP (`cognito-direct-auth.ts`) —
 * no redirect to the Cognito-hosted Managed Login page. "Continue with SSO"
 * is a disabled placeholder: no identity federation is configured yet
 * (ADR-0011 remains unchanged on that point). Once a session is obtained it
 * is handed to `direct-session-bridge.ts`, which stores it exactly where
 * `react-oidc-context`'s `<AuthProvider>` reads from and hard-navigates —
 * every downstream consumer (`useAuth`, `ProtectedRoute`, logout, silent
 * renew) is unaware this page exists.
 */

const TYPING_WORDS = [
	'decks.',
	'carousels.',
	'campaigns.',
	'copy.',
	'briefs.',
	'posts.',
	'landings.',
	'socials.',
	'ads.',
]

/** Cycles through {@link TYPING_WORDS} with a type/delete animation, matching the Claude Design mockup's hero headline. */
function useTypingWord(): string {
	const [text, setText] = useState('')

	useEffect(() => {
		let wordIndex = 0
		let charIndex = 0
		let deleting = false
		let timer: ReturnType<typeof setTimeout>

		const tick = () => {
			const current = TYPING_WORDS[wordIndex]
			if (!deleting) {
				charIndex++
				setText(current.slice(0, charIndex))
				if (charIndex === current.length) {
					deleting = true
					timer = setTimeout(tick, 1600)
					return
				}
			} else {
				charIndex--
				setText(current.slice(0, charIndex))
				if (charIndex === 0) {
					deleting = false
					wordIndex = (wordIndex + 1) % TYPING_WORDS.length
					timer = setTimeout(tick, 300)
					return
				}
			}
			timer = setTimeout(tick, deleting ? 50 : 90)
		}

		timer = setTimeout(tick, 600)
		return () => clearTimeout(timer)
	}, [])

	return text
}

/** The mockup this page is ported from is a fixed, non-scrolling single screen (`body { overflow: hidden; height: 100vh }`). Locking `<body>` here — rather than in Login.css — keeps the effect scoped to this page's lifetime instead of leaking into every other route once the stylesheet is loaded. */
function useLockBodyScroll() {
	useEffect(() => {
		const previousOverflow = document.body.style.overflow
		document.body.style.overflow = 'hidden'
		return () => {
			document.body.style.overflow = previousOverflow
		}
	}, [])
}

function LoginHero({ typingWord }: { typingWord: string }) {
	return (
		<section className="signin-hero">
			<div className="signin-hero-orb signin-hero-orb--red" />
			<div className="signin-hero-orb signin-hero-orb--purple" />
			<div className="signin-hero__top">
				<svg
					xmlns="http://www.w3.org/2000/svg"
					viewBox="0 0 256 256"
					width="36"
					height="36"
					aria-label="Keenfolks K mark"
					className="text-ds-primary-foreground"
				>
					{/* `currentColor` so the mark follows the token on its className rather than
					    restating a literal white that a theme change would leave behind. */}
					<g fill="currentColor">
						<path d="M14,16 H58 V144 L14,188 Z" />
						<path d="M196,16 H240 V78 L78,240 H14 L196,58 Z" />
						<path d="M241,240 H196 V184 L133,121 L165,89 L241,164 Z" />
					</g>
				</svg>
				<p className="signin-hero__overline">KEENFOLKS X_ &nbsp;·&nbsp; ATLAS</p>
			</div>

			<div className="signin-hero__center">
				<h1 className="signin-hero__headline">
					<span className="signin-hero__line1">the content factory</span>
					<span className="signin-hero__line2">
						<span>for</span>{' '}
						<span className="signin-typing-word">
							{typingWord}
							<span className="signin-typing-cursor" />
						</span>
					</span>
				</h1>
			</div>

			<div className="signin-hero__bottom">
				<p className="signin-hero__caption">KF—2026 / 05 / 22 &nbsp;·&nbsp; internal access only_</p>
			</div>
		</section>
	)
}

export function LoginPage() {
	const { isAuthenticated, isLoading } = useAuth()
	const navigate = useNavigate()
	const typingWord = useTypingWord()
	const flow = useLoginFlow()
	useLockBodyScroll()

	useEffect(() => {
		if (isAuthenticated) {
			void navigate(takeIntendedRoute(), { replace: true })
		}
	}, [isAuthenticated, navigate])

	if (isLoading) {
		return <LoadingScreen />
	}

	return (
		<div className="signin-shell">
			<LoginHero typingWord={typingWord} />

			<section className="signin-form-panel">
				<div className="signin-form-card">
					{flow.step === 'forgot' || flow.step === 'forgotConfirm' ? (
						<div className="signin-form-card__head">
							<p className="signin-form-card__lead">Reset your password.</p>
						</div>
					) : (
						<div className="signin-form-card__head">
							<h2 className="signin-form-card__title">
								Brief in,
								<br />
								content <em>out</em>
							</h2>
							<p className="signin-form-card__lead">The factory floor for every asset</p>
						</div>
					)}

					<div className="signin-auth-box">
						{flow.step === 'options' && <OptionsStep onContinueWithEmail={flow.goToEmail} />}

						{flow.step === 'email' && (
							<EmailStep
								email={flow.email}
								setEmail={flow.setEmail}
								password={flow.password}
								setPassword={flow.setPassword}
								error={flow.error}
								info={flow.info}
								busy={flow.busy}
								onSubmit={flow.onSignIn}
								onForgot={flow.goToForgot}
								onBack={flow.goToOptions}
							/>
						)}

						{flow.step === 'newPassword' && (
							<NewPasswordStep
								newPassword={flow.newPassword}
								setNewPassword={flow.setNewPassword}
								error={flow.error}
								busy={flow.busy}
								onSubmit={flow.onCompleteNewPassword}
							/>
						)}

						{flow.step === 'forgot' && (
							<ForgotStep
								resetEmail={flow.resetEmail}
								setResetEmail={flow.setResetEmail}
								error={flow.error}
								busy={flow.busy}
								onSubmit={flow.onForgotPassword}
								onBack={flow.goToEmail}
							/>
						)}

						{flow.step === 'forgotConfirm' && (
							<ForgotConfirmStep
								resetEmail={flow.resetEmail}
								resetCode={flow.resetCode}
								setResetCode={flow.setResetCode}
								resetNewPassword={flow.resetNewPassword}
								setResetNewPassword={flow.setResetNewPassword}
								error={flow.error}
								busy={flow.busy}
								onSubmit={flow.onConfirmForgotPassword}
								onBack={flow.backToForgot}
							/>
						)}
					</div>
				</div>
			</section>
		</div>
	)
}
